/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/** @jsx jsx */

import { jsx } from '@emotion/react'
import { useEffect, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { debounce } from 'lodash'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFilterGroup,
  EuiFilterButton,
  EuiSearchBar,
  EuiPopover,
  EuiSelectable,
  EuiFormRow,
  EuiIcon,
  useCurrentEuiBreakpoint,
  EuiText,
} from '@elastic/eui'
import type { EuiSelectableOption } from '@elastic/eui'

import type { AnyProject } from '@modules/project-api/types'
import type { AnyRegion } from '@modules/cluster-api/types'
import { getShortPlatformNameByRegionId } from '@modules/utils/platform'

import { ProjectCpsAutoCompleteInput } from './ProjectCpsAutoCompleteInput'
import { filterableFields } from './constants'

import type { SearchableProject, FilterableField, FilterStrings, FilterOptions } from './types'

const messages = defineMessages({
  searchPlaceholder: {
    id: 'cps.searchbar.placeholder',
    defaultMessage: 'Search',
  },
  filterEmpty: {
    id: 'cps.searchbar.filterEmpty',
    defaultMessage: 'No filters available',
  },
})

const filterMessages = defineMessages({
  projectType: {
    id: 'cps.searchbar.filter.projectType',
    defaultMessage: 'Project type',
  },
  cloudProvider: {
    id: 'cps.searchbar.filter.cloudProvider',
    defaultMessage: 'Cloud provider',
  },
  region: {
    id: 'cps.searchbar.filter.region',
    defaultMessage: 'Region',
  },
  tags: {
    id: 'cps.searchbar.filter.tags',
    defaultMessage: 'Tags',
  },
})

const getEmptyFilter = (): FilterStrings => {
  const result = {} as FilterStrings

  for (const filter of filterableFields) {
    result[filter] = []
  }

  return result
}

const getEmptyFilterOptions = (): FilterOptions => {
  const result = {} as FilterOptions

  for (const filter of filterableFields) {
    result[filter] = []
  }

  return result
}

export const convertProjectsToSearchableProjects = (
  projects: AnyProject[],
  regions: AnyRegion[],
): SearchableProject[] =>
  projects.map((project) => {
    const region = regions.find((item) => item.id === project.region_id)
    const cloudProvider = region && getShortPlatformNameByRegionId(region.csp)
    const tags: string[] = Object.entries(project.metadata.tags ?? {}).map(
      ([key, value]) => `${key}:${value}`,
    )

    return {
      id: project.id,
      name: project.name,
      alias: project.alias,
      projectType: project.type,
      cloudProvider,
      region: region?.name,
      tags,
    }
  })

export const convertProjectsToFilterStrings = (
  projects: SearchableProject[],
  selectedFilter?: FilterStrings,
): FilterStrings => {
  const filterStrings: FilterStrings = getEmptyFilter()

  // Populate a dictionary of filter key and their values
  for (const project of projects) {
    for (const field of filterableFields) {
      const value = project[field]

      if (Array.isArray(value)) {
        filterStrings[field].push(...value)
      }

      // Also merge the existing selected filter to avoid stale
      // filters if the projects fields change
      if (Array.isArray(selectedFilter?.[field])) {
        filterStrings[field].push(...selectedFilter[field])
      }

      if (typeof value === 'string' && value) {
        filterStrings[field].push(value)
      }
    }
  }

  // De-dupe the filter values
  for (const field in filterStrings) {
    if (Array.isArray(filterStrings[field])) {
      filterStrings[field] = [...new Set(filterStrings[field])]
    }
  }

  return filterStrings
}

export const convertProjectsToFilterOptions = (
  projects: SearchableProject[],
  selectedFilter: FilterStrings,
): FilterOptions => {
  const filterOptions: FilterOptions = getEmptyFilterOptions()
  const filterStrings = convertProjectsToFilterStrings(projects, selectedFilter)

  for (const field of filterableFields) {
    const filterStringArray = filterStrings[field]

    for (const filterString of filterStringArray) {
      const isSelected = selectedFilter[field].includes(filterString)
      filterOptions[field].push({
        label: filterString,
        checked: isSelected ? 'on' : undefined,
        prepend: field === 'cloudProvider' && <EuiIcon type={`logo${filterString}`} />,
      })
    }
  }

  for (const field of filterableFields) {
    // Filters the options by 'on' first, the alphabetically
    filterOptions[field].sort((a, b) => {
      if (a.checked === 'on' && b.checked !== 'on') {
        return -1
      }

      if (a.checked !== 'on' && b.checked === 'on') {
        return 1
      }

      return `${a.label}`.localeCompare(`${b.label}`, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    })
  }

  return filterOptions
}

export const toggleFilterStrings = (
  field: FilterableField,
  option: EuiSelectableOption,
  selectedFilter: FilterStrings,
): FilterStrings => {
  const filterArray = selectedFilter[field]
  const { label, checked } = option

  if (checked === 'on') {
    return {
      ...selectedFilter,
      [field]: [...filterArray, label],
    }
  }

  return {
    ...selectedFilter,
    [field]: filterArray.filter((option) => option !== label),
  }
}

const filterProjects = (
  query: string,
  selectedFilters: FilterStrings,
  searchableProjects: SearchableProject[],
  onFilter: (projectIds: string[] | null) => void,
  onError: (error: Error | null) => void,
) => {
  const { Query } = EuiSearchBar
  let filterQuery = query

  for (const filter of filterableFields) {
    const selectedFilterStrings = selectedFilters[filter]

    if (!selectedFilterStrings.length) {
      continue
    }

    const filterStringsWithQuotes = selectedFilterStrings.map((string) => `"${string}"`)

    if (filterStringsWithQuotes.length === 1) {
      filterQuery = `${filterQuery} ${filter}:${filterStringsWithQuotes[0]}`
    } else {
      filterQuery = `${filterQuery} ${filter}:(${filterStringsWithQuotes.join(' or ')})`
    }
  }

  if (!filterQuery) {
    onFilter(null)
    onError(null)
    return
  }

  try {
    const filteredProjects = Query.execute(filterQuery.trim(), searchableProjects)
    onFilter(filteredProjects.map((project) => project.id))
    onError(null)
  } catch (e) {
    if (e instanceof Error) {
      onError(e)
    }
  }
}

const debouncedFilterProject = debounce(filterProjects, 250)

interface ProjectCpsFilterButtonProps {
  label: string
  type: FilterableField
  options?: EuiSelectableOption[]
  selectedOptions?: string[]
  isOpen: boolean
  onButtonClick: () => void
  onOptionClick: (option: EuiSelectableOption) => void
  onPopoverClose: () => void
}

const ProjectCpsFilterButton = (props: ProjectCpsFilterButtonProps) => {
  const {
    label,
    type,
    options = [],
    selectedOptions = [],
    isOpen,
    onButtonClick,
    onOptionClick,
    onPopoverClose,
  } = props

  const { formatMessage } = useIntl()

  const button = (
    <EuiFilterButton
      data-test-id={`project-cps-searchbar-filter-button-${type}`}
      iconType='arrowDown'
      onClick={onButtonClick}
      isSelected={isOpen}
      numFilters={options.length}
      hasActiveFilters={!!selectedOptions.length}
      numActiveFilters={selectedOptions.length}
    >
      {label}
    </EuiFilterButton>
  )

  return (
    <EuiPopover button={button} isOpen={isOpen} closePopover={onPopoverClose}>
      <EuiSelectable
        data-test-id='project-cps-searchbar-filter-selectable'
        options={options}
        onChange={(_options, _e, option) => onOptionClick(option)}
        emptyMessage={formatMessage(messages.filterEmpty)}
      >
        {(list) => <div style={{ width: 300, maxHeight: 400, overflow: 'auto' }}>{list}</div>}
      </EuiSelectable>
    </EuiPopover>
  )
}

interface ProjectCpsSearchBarProps {
  projects: AnyProject[]
  searchPlaceholder?: string
  regions?: AnyRegion[]
  onProjectsFilter: (projectIds: string[] | null) => void
}

export const ProjectCpsSearchbar = (props: ProjectCpsSearchBarProps) => {
  const { projects, regions = [], searchPlaceholder, onProjectsFilter } = props

  const [openFilter, setOpenFilter] = useState<FilterableField>()
  const [query, setQuery] = useState<string>('')
  const [selectedFilters, setSelectedFilters] = useState<FilterStrings>(getEmptyFilter())
  const [error, setError] = useState<Error | null>(null)

  const breakpoint = useCurrentEuiBreakpoint()

  const { formatMessage } = useIntl()

  const searchableProjects = useMemo(
    () => convertProjectsToSearchableProjects(projects, regions),
    [projects, regions],
  )

  const filterOptions = useMemo<FilterOptions>(
    () => convertProjectsToFilterOptions(searchableProjects, selectedFilters),
    [searchableProjects, selectedFilters],
  )

  const onQueryChange = (value: string) => {
    setQuery(value)
    setError(null)
  }

  const onPopoverToggle = (filter: FilterableField) => {
    setOpenFilter(openFilter === filter ? undefined : filter)
  }

  const onPopoverClose = () => {
    setOpenFilter(undefined)
  }

  const onOptionClick = (field: FilterableField, option: EuiSelectableOption) => {
    setSelectedFilters(toggleFilterStrings(field, option, selectedFilters))
  }

  useEffect(() => {
    debouncedFilterProject(query, selectedFilters, searchableProjects, onProjectsFilter, setError)
    return () => debouncedFilterProject.cancel()
  }, [query, selectedFilters, searchableProjects])

  return (
    <EuiFlexGroup
      data-test-id='project-cps-searchbar'
      direction={breakpoint === 'xl' ? 'row' : 'column'}
      gutterSize='m'
    >
      <EuiFlexItem>
        <EuiFormRow isInvalid={!!error} error={error?.message} fullWidth={true}>
          <ProjectCpsAutoCompleteInput
            query={query}
            onQueryChange={onQueryChange}
            placeholder={searchPlaceholder || formatMessage(messages.searchPlaceholder)}
            searchableProjects={searchableProjects}
            error={error}
          />
        </EuiFormRow>
        {!error && (
          // Hidden element to prevent elements from jumping when the form row
          // Renders the error message
          <EuiText size='s' css={{ visibility: 'hidden' }} aria-hidden={true}>
            hidden
          </EuiText>
        )}
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiFilterGroup>
          {filterableFields.map((field) => (
            <ProjectCpsFilterButton
              key={field}
              type={field}
              label={formatMessage(filterMessages[field])}
              options={filterOptions[field]}
              selectedOptions={selectedFilters[field]}
              isOpen={openFilter === field}
              onButtonClick={() => onPopoverToggle(field)}
              onOptionClick={(option) => onOptionClick(field, option)}
              onPopoverClose={onPopoverClose}
            />
          ))}
        </EuiFilterGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}
